/**
 * The composer's agent state: what a chat runs on and what a pick writes where. Pure —
 * `useHarnessControl` feeds it and applies what it returns — so every host, and every send path,
 * reads one answer.
 *
 * Phases: draft (no session) → minted (a session with no turns) → started (a turn has run, or the
 * session cannot be re-stamped: not loaded, flag off, no catalog). Orthogonal: awaiting-connect,
 * the pick is a subscription with no credential; nothing sends until it connects. A minted
 * session cannot be stamped with an unconnected subscription (the server refuses it), so it stays
 * on Cohesive and the pick is held beside it until the credential lands.
 *
 * Transitions (event · from → to · what is written where):
 *   pick agent    · draft → draft                   · draft := agent at its default model
 *   pick agent    · minted → minted                 · session := harness + default model; draft follows the landed row
 *   pick agent    · minted → minted+awaiting        · held := agent at its default model; draft := held
 *   pick agent    · minted+awaiting → minted        · (the session's own agent) held cleared; draft := the row
 *   pick model    · draft → draft                   · draft := harness + model, thinking dropped
 *   pick model    · minted → minted                 · session := harness + model; draft follows the landed row
 *   pick model    · minted+awaiting → same          · held := model, thinking dropped; draft := held
 *   pick model    · started → started               · session := model; sends carry it until it lands; draft untouched
 *   pick thinking · draft → draft                   · draft := + thinking
 *   pick thinking · minted → minted                 · session := thinking; draft follows the landed row
 *   pick thinking · minted+awaiting → same          · held := + thinking; draft := held
 *   pick thinking · started → started               · session := thinking; draft untouched
 *   pick tier     · started → started (Cohesive)    · session := tier (before the first turn the host's tier rides the message)
 *   mint          · draft → minted                  · session stamped with the draft by the create
 *   mint          · draft+awaiting → minted+awaiting · session stamped Cohesive by the create; held := the draft
 *   connect lands · minted+awaiting → minted        · session := harness + held model + held thinking, one write; held cleared; draft follows
 *   first turn    · minted → started                · (server) the agent locks
 *   write refused · any                             · row refetched, so the pick shown ahead of it rolls back; draft untouched; a send waiting on it fails
 */
import {
  AgentCapability,
  AgentId,
  agentOf,
  findAgent,
  isCohesiveHarness,
} from "@/constants/agents";
import {
  defaultEffortFor,
  defaultModelFor,
  harnessSeatVisible,
  ModelCatalog,
  modelSwitchAllowed,
  runsOnHarness,
} from "@/constants/harnesses";
import { agentDraft, HarnessDraft } from "@/hooks/useHarnessDraft";
import {
  AgentReasoningEffort,
  tierOf,
} from "@/pages/canvas/hooks/useCanvasReasoningEffort";
import {
  FlowSession,
  HarnessCapability,
  HarnessCapabilityAlias,
  HarnessCapabilityEffort,
  HarnessCapabilityModel,
  HarnessId,
  PostMessageParams,
  UpdateFlowSessionRequest,
} from "@cohesive/api-client";

export type AgentPhase = "draft" | "minted" | "started";

export interface AgentStateInput {
  flowSessionId?: string;
  /** The session row, optimistic picks included. */
  session?: FlowSession;
  /** The surface's new-chat pick. */
  draft?: HarnessDraft;
  /** Whether the host persists the draft; without one a session-less control has nothing to drive. */
  hasDraftOwner: boolean;
  /** A minted session's pick waiting on Connect. */
  held?: HarnessDraft;
  agents?: AgentCapability[];
  capabilities?: HarnessCapability[];
  harnessRuntime: boolean;
  /** A started chat's model write still in flight. */
  pendingModel?: string;
  /** The host's Cohesive tier, which rides a message until the chat has started. */
  hostTier?: AgentReasoningEffort;
  /** This page load's catalog is still on its way (not failed: a failure falls to Cohesive). */
  catalogPending?: boolean;
}

/** The agent part of a message's params. */
export type AgentSendSettings = Pick<
  PostMessageParams,
  "reasoning_effort" | "harness_reasoning_effort" | "model"
>;

/** One row of the model menu: the harness's own model, plus whether this chat may move to it. */
export interface HarnessModelOption extends HarnessCapabilityModel {
  disabled: boolean;
}

export interface AgentView {
  phase: AgentPhase;
  /** The pick a minted session holds until its subscription connects (stored, or the draft's). */
  held: HarnessDraft | undefined;
  awaitingConnect: boolean;
  /**
   * A draft whose catalog is still loading: the agent is not known yet, so nothing may mint a
   * chat on it (useHarnessDraft's `ready` is the same signal for the minting paths).
   */
  pending: boolean;
  /** Whether any send may go: false while awaiting Connect or pending. */
  canSend: boolean;
  /** Whether the agent seat has anything to state or drive. */
  visible: boolean;
  /** The agent is read-only: the chat has started, or cannot be re-stamped. */
  locked: boolean;
  /** Whether the chip opens the agent menu: unlocked, and more than one agent on offer. */
  switchable: boolean;
  agent: AgentId;
  agents: AgentCapability[];
  agentCapability: AgentCapability | undefined;
  /** Whether a message can be sent on the agent: false for an unconnected subscription. */
  connected: boolean;
  harness: HarnessId;
  capability: HarnessCapability | undefined;
  /** The selected model, or undefined on Cohesive, whose tier picks one server-side. */
  model: string | undefined;
  /** The model menu's rows, in catalog order, with the ones this chat can't move to disabled. */
  models: HarnessModelOption[];
  aliases: HarnessCapabilityAlias[];
  /** Whether any row is disabled, so the menu can say once why. */
  modelSwitchLocked: boolean;
  /** The selected model's efforts; empty where it takes none. */
  efforts: HarnessCapabilityEffort[];
  /** The thinking the next message runs on; undefined on Cohesive and where the model takes none. */
  effort: string | undefined;
  /** A started Cohesive chat's tier, off its row; undefined elsewhere, where the host's applies. */
  tier: AgentReasoningEffort | undefined;
  /** The tier the pill shows and a message sends: the row's, else the host's where the row offers it, else the row's default. */
  shownTier: AgentReasoningEffort;
  sendSettings: AgentSendSettings;
}

export type AgentEvent =
  | { type: "agent"; agent: AgentId }
  | { type: "model"; model: string }
  | { type: "effort"; effort: string }
  | { type: "tier"; tier: AgentReasoningEffort }
  /** The inputs moved: a mint to adopt the draft's held pick, or a credential that landed. */
  | { type: "sync" };

export interface AgentEffects {
  draft?: HarnessDraft;
  /** Set the held pick; null clears it. */
  held?: HarnessDraft | null;
  write?: UpdateFlowSessionRequest;
}

function phaseOf(input: AgentStateInput): AgentPhase {
  if (!input.flowSessionId) return "draft";
  // Status is the zero-turn proxy — a session leaves `initialized` when its first message lands —
  // and the server enforces the real invariant (422) if this view is stale.
  const restampable =
    input.session?.status === "initialized" &&
    input.harnessRuntime &&
    !!input.agents;
  return restampable ? "minted" : "started";
}

/** The draft a minted session's row stands for: the pick it holds now. */
function rowDraft(session: FlowSession): HarnessDraft {
  if (isCohesiveHarness(session.harness)) {
    return { harness: session.harness as HarnessId };
  }
  return {
    harness: session.harness as HarnessId,
    model: session.model,
    ...(session.reasoning_effort ? { effort: session.reasoning_effort } : {}),
  };
}

/**
 * What the draft follows once a write lands: the row while it still has no turns, nothing once it
 * has (a started chat's picks are that chat's alone).
 */
export function landedDraft(session: FlowSession): HarnessDraft | undefined {
  return session.status === "initialized" ? rowDraft(session) : undefined;
}

function heldOf(input: AgentStateInput, phase: AgentPhase) {
  if (phase !== "minted") return undefined;
  if (input.held) return input.held;
  // A draft's unconnected pick, minted early (an attach) onto Cohesive by the create, still waits.
  const { draft, session, agents } = input;
  if (!draft || !isCohesiveHarness(session?.harness)) return undefined;
  if (isCohesiveHarness(draft.harness)) return undefined;
  const agent = findAgent(agents, agentOf(draft.harness));
  return agent && !agent.connected ? draft : undefined;
}

export function agentView(input: AgentStateInput): AgentView {
  const { session, draft, agents, capabilities } = input;
  const phase = phaseOf(input);
  const held = heldOf(input, phase);
  const locked = phase === "started";

  const harness =
    held?.harness ??
    (phase === "draft" ? draft?.harness : session?.harness) ??
    "cohesive";
  const agent = agentOf(harness);
  const agentCapability = findAgent(agents, agent);
  const onCohesive = isCohesiveHarness(harness);
  const connected = onCohesive || !!agentCapability?.connected;
  const capability = capabilities?.find((c) => c.id === harness);
  // A subscription agent's catalog is its row; a legacy session on a harness that is no agent's
  // (opencode) reads that harness's row.
  const catalog: ModelCatalog | undefined =
    agentCapability?.harness === harness ? agentCapability : capability;

  const model = onCohesive
    ? undefined
    : phase === "draft"
      ? draft?.model
      : ((held ? held.model : session?.model) ?? defaultModelFor(catalog));

  // A pick is bound by nothing but the catalog until the first turn — the lock belongs to a
  // sandbox that resolved its provider credentials once, and a chat with no messages has none.
  const models: HarnessModelOption[] = onCohesive
    ? []
    : (catalog?.models ?? []).map((option) => ({
        ...option,
        disabled: locked && !modelSwitchAllowed(capability, model, option.id),
      }));

  // Without a session the seat needs a draft owner to drive; with one it needs the row loaded,
  // never the draft: the stamp may already be spent.
  const hasSomethingToSay = input.flowSessionId
    ? !!session
    : input.hasDraftOwner;
  const visible =
    hasSomethingToSay &&
    harnessSeatVisible(session?.harness, input.harnessRuntime, capabilities);

  const efforts =
    catalog?.models?.find((option) => option.id === model)?.efforts ?? [];
  const offered = (id: string | undefined) =>
    efforts.some((option) => option.id === id);
  // A draft's thinking reaches the session minted from it, on the draft's own agent and model.
  const draftEffort =
    draft?.effort && draft.harness === harness && draft.model === model
      ? draft.effort
      : undefined;
  const effort = onCohesive
    ? undefined
    : (
        {
          draft: [draft?.effort],
          minted: held
            ? [held.effort]
            : [session?.reasoning_effort, draftEffort],
          started: [session?.reasoning_effort],
        }[phase] as (string | undefined)[]
      )
        .concat(defaultEffortFor(catalog, model))
        .find(offered);

  // The host's tier was picked on whichever Cohesive row was listed then; one this row does not
  // offer would be refused, so it runs the row's default.
  const rowTiers = onCohesive ? agentCapability?.tiers : undefined;
  const hostTier =
    !rowTiers || rowTiers.some((option) => option.id === input.hostTier)
      ? input.hostTier
      : ((agentCapability?.default_tier_id as
          | AgentReasoningEffort
          | undefined) ?? "standard");
  // A stamped tier the session's own row no longer offers runs that row's default.
  const offersTier = (value: AgentReasoningEffort | undefined) =>
    !!value &&
    (agentCapability?.harness !== session?.harness ||
      !rowTiers ||
      rowTiers.some((option) => option.id === value));
  const stampedTier = tierOf(session?.reasoning_effort);
  const tier =
    locked && onCohesive && session && runsOnHarness(session.harness)
      ? offersTier(stampedTier)
        ? stampedTier
        : "standard"
      : undefined;

  const awaitingConnect = !connected;
  const pending = phase === "draft" && !!input.catalogPending;
  const sendModel = locked && !onCohesive ? input.pendingModel : undefined;

  return {
    phase,
    held,
    awaitingConnect,
    pending,
    canSend: !awaitingConnect && !pending,
    visible,
    locked,
    switchable:
      visible && !locked && input.harnessRuntime && (agents?.length ?? 0) > 1,
    agent,
    agents: agents ?? [],
    agentCapability,
    connected,
    harness: harness as HarnessId,
    capability,
    model,
    models,
    aliases: onCohesive ? [] : (agentCapability?.aliases ?? []),
    modelSwitchLocked: models.some((option) => option.disabled),
    efforts,
    effort,
    tier,
    shownTier: tier ?? hostTier ?? "standard",
    // Cohesive's tier rides `reasoning_effort`, a subscription's thinking
    // `harness_reasoning_effort`; the ids overlap, so each goes only on its own agent.
    sendSettings: {
      reasoning_effort: onCohesive ? (tier ?? hostTier) : undefined,
      harness_reasoning_effort: onCohesive ? undefined : effort,
      ...(sendModel ? { model: sendModel } : {}),
    },
  };
}

/** What an event writes, and where, per the table above. */
export function agentTransition(
  input: AgentStateInput,
  event: AgentEvent,
): AgentEffects {
  const view = agentView(input);
  const { phase, held } = view;
  const session = input.session;

  switch (event.type) {
    case "agent": {
      // Picking the row already checked is how the menu is dismissed; it must not reset the model.
      if (event.agent === view.agent) return {};
      const target = findAgent(input.agents, event.agent);
      if (!target) return {};
      const picked = agentDraft(target);
      if (phase === "draft") return { draft: picked };
      if (phase === "started" || !session) return {};
      if (event.agent === agentOf(session.harness)) {
        return { held: null, draft: rowDraft(session) };
      }
      if (!target.connected) return { held: picked, draft: picked };
      return {
        held: null,
        write: { harness: picked.harness, model: picked.model },
      };
    }
    case "model": {
      // Landing on what the pill already shows pins nothing: on a chat that pinned no model that
      // is the resolved default, deliberately left to resolve per turn.
      if (!view.model || event.model === view.model) return {};
      const picked = { harness: view.harness, model: event.model };
      if (phase === "draft") return { draft: picked };
      if (held) return { held: picked, draft: picked };
      if (phase === "minted") return { write: picked };
      return { write: { model: event.model } };
    }
    case "effort": {
      if (!view.model) return {};
      const picked = {
        harness: view.harness,
        model: view.model,
        effort: event.effort,
      };
      if (phase === "draft") return { draft: picked };
      if (held) return { held: picked, draft: picked };
      return { write: { reasoning_effort: event.effort } };
    }
    case "tier": {
      if (view.tier === undefined || event.tier === view.tier) return {};
      return { write: { reasoning_effort: event.tier } };
    }
    case "sync": {
      if (!held) return {};
      if (view.connected) {
        return {
          held: null,
          write: {
            harness: held.harness,
            model: view.model,
            ...(view.effort && view.effort === held.effort
              ? { reasoning_effort: view.effort }
              : {}),
          },
        };
      }
      return input.held ? {} : { held };
    }
  }
}
